import { useEffect, useState } from 'react';
import { ArrowLeft, AlertCircle, CheckCircle2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { isDemoProjectId } from '../lib/demoProject';
import { navigateToHelp, type HelpSection } from '../lib/helpNavigation';
import { Project } from '../types/project';
import { UtilityRequestPrompt } from './UtilityRequestPrompt';
import {
  routesFor,
  stepsFor,
  describeHistory,
  safePortalUrl,
  type LinkRoute,
  type RouteSource,
  type SentRoute,
} from '../lib/utilityRoutes';
import { pilotIsLive, PILOT_DATA_ASK, type UtilityPilot } from '../lib/utilityPilot';
import { isBurlingtonElectric, joinBedAccount } from '../lib/customerRequest';
import { isUsableTemplate, REQUEST_FORM_BUSINESS_DAYS } from '../lib/requestForm';

/**
 * The Get data page. One button on the Existing Load tab opens it.
 *
 * Rebuilt 2026-09-12 to the UX review's mockup (context/ux.md): the utility and
 * its route on one line, three steps that say what happens, and a send box
 * whose only real field is the customer's email. The route is stated once. The
 * customer-facing download steps sit behind a preview link, and the
 * alternatives are one line each. The page still ends with the demand-capture
 * prompt in its original framing ("Ask TrueLoad to connect with this utility"):
 * utilities.md LOCKED says a utility with no route is a lead, not a dead end,
 * and every no-connect page must capture that demand.
 *
 * Three test ids carry over because the guided demo drives the flow by them:
 * ami-request-modal (the page), btn-send-ami-request, and — on the tab —
 * ami-request-sent. A send closes the page; the confirmation and the request's
 * status live on the tab, where the contractor comes back to them.
 */

interface GetDataPageProps {
  projectId: string;
  project: Partial<Project>;
  /** The utility's row, or null for "Other (not listed)". */
  utility: RouteSource | null;
  onClose: () => void;
  onSuccess: (expiresAt: string, dataRoute: SentRoute) => void;
}

// Width is left to the call site: the email field fills its column, the
// account fields stay narrow.
const INPUT =
  'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

/** How long the link lives on each route, in hours. Mirrors send-ami-consent-request. */
const LINK_HOURS: Record<LinkRoute, number> = {
  email_request: 72,
  self_download: 14 * 24,
  customer_request: 30 * 24,
};

export function GetDataPage({ projectId, project, utility, onClose, onSuccess }: GetDataPageProps) {
  const { user, userProfile, companyProfile } = useAuth();

  const routes = routesFor(utility);
  const utilityName = (utility?.name ?? project.ami_data_provider ?? '').trim();
  const utilityEmail = (utility?.ami_contact_email ?? '').trim();
  const { steps, generic } = stepsFor(utility);
  const portal = safePortalUrl(utility?.portal_url);
  const history = describeHistory(utility?.history_months);
  const format = (utility?.export_format ?? '').trim();
  const rollout = (utility?.rollout_note ?? '').trim();
  const verified = utility?.instructions_verified === true;

  const serviceAddress = [
    project.customer_address,
    project.customer_city,
    project.customer_state,
    project.customer_zip,
  ]
    .filter(Boolean)
    .join(', ');

  const contractorName = companyProfile?.preparer_name || userProfile?.full_name || '';
  const companyName = companyProfile?.company_name || '';
  const contractorEmail = project.preparer_email || userProfile?.email || user?.email || '';

  // The route the link takes. The utility's status picks the default; where a
  // utility offers both, the switch under its name changes it.
  const [dataRoute, setDataRoute] = useState<LinkRoute | null>(routes.primary);
  const [previewOpen, setPreviewOpen] = useState(false);

  const projectName = (project.customer_name || '').trim();
  const [customerName, setCustomerName] = useState(projectName);
  const [customerEmail, setCustomerEmail] = useState(project.customer_email || '');
  const [accountNumber, setAccountNumber] = useState(project.utility_account_number || '');
  const [bedCustomerId, setBedCustomerId] = useState(project.customer_id || '');
  const [bedLocationId, setBedLocationId] = useState(project.location_id || '');
  const [editingContact, setEditingContact] = useState(false);
  const [customUtilityEmail, setCustomUtilityEmail] = useState(utilityEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isBED = isBurlingtonElectric(utilityName);
  const effectiveUtilityEmail = editingContact ? customUtilityEmail.trim() : utilityEmail;
  const bothRoutes = routes.selfDownload && routes.emailRequest;
  // customer_request with the utility's own web form: the customer submits it
  // and the utility sends the file to the contractor. No address is needed.
  const formRoute = dataRoute === 'customer_request' && isUsableTemplate(utility?.request_form_template);
  // Whether a form request sent now gets a TrueLoad receiving address
  // (app_config 'request_form_receiving_enabled'; the send function decides).
  const [receivingOn, setReceivingOn] = useState(false);
  useEffect(() => {
    if (!formRoute) return;
    let cancelled = false;
    void supabase
      .from('app_config')
      .select('value')
      .eq('key', 'request_form_receiving_enabled')
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setReceivingOn(String(data?.value ?? '').trim() === 'true');
      });
    return () => {
      cancelled = true;
    };
  }, [formRoute]);

  // Utility contact pilot: for a pilot firm on this utility, TrueLoad asks the
  // utility's contact directly and the customer is not involved. RLS returns
  // only the caller's own firm's rows; the send function checks again.
  const [pilot, setPilot] = useState<UtilityPilot | null>(null);
  useEffect(() => {
    const utilityId = project.utility_id;
    if (!utilityId || isDemoProjectId(projectId)) return;
    let cancelled = false;
    void supabase
      .from('utility_data_pilots')
      .select('*')
      .eq('utility_id', utilityId)
      .then(({ data }) => {
        if (cancelled) return;
        setPilot(((data ?? []) as UtilityPilot[]).find((p) => pilotIsLive(p)) ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [project.utility_id, projectId]);

  async function handlePilotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!customerName.trim()) {
      setError('The customer’s name is needed: the utility looks the customer up by it.');
      return;
    }
    if (!serviceAddress) {
      setError('The service address is needed. Add it under Project Info.');
      return;
    }
    if (!accountNumber.trim()) {
      setError('The account number is needed: it is how the utility finds the meter. It is on the customer’s bill.');
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Your session has expired. Please log in again.');
        return;
      }
      const response = await fetch('/.netlify/functions/send-ami-consent-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          projectId,
          contractorName,
          contractorLicense: companyProfile?.preparer_license || '',
          companyName,
          contractorEmail,
          customerName: customerName.trim(),
          customerPhone: (project.customer_phone || '').replace(/\D/g, '') || null,
          serviceAddress,
          utilityId: project.utility_id ?? null,
          accountNumber: accountNumber.trim(),
          dataRoute: 'utility_request',
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        setError(result.error || 'The request could not be sent. Please try again.');
        return;
      }
      if (!projectId.startsWith('local_')) {
        const updates: Record<string, string> = { utility_account_number: accountNumber.trim() };
        if (!projectName && customerName.trim()) updates.customer_name = customerName.trim();
        await supabase.from('projects').update(updates).eq('id', projectId);
      }
      onSuccess(result.expiresAt, 'utility_request');
    } catch (err) {
      console.error('Get data pilot send error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  function openHelp(section: HelpSection) {
    // The help view replaces the project view, and this page with it.
    onClose();
    navigateToHelp(section);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!dataRoute) return;

    if (!customerName.trim()) {
      setError('The customer’s name is needed: the email is addressed to them.');
      return;
    }
    if (!customerEmail) {
      setError('Customer email is required: the link goes to that address.');
      return;
    }
    if (dataRoute === 'email_request' && !effectiveUtilityEmail) {
      setError('A utility contact email is required for an email request.');
      return;
    }
    if (dataRoute === 'customer_request' && !formRoute && !effectiveUtilityEmail) {
      setError('The utility’s request address is needed: your customer’s email goes there.');
      return;
    }

    setLoading(true);

    // The demo runs on a fabricated job with a real-looking customer email, and
    // this form is one the demo actively invites people to poke at. Sending for
    // real would put a genuine request, naming a contractor, an address and a
    // utility, in a real inbox. So the demo never reaches the function: it
    // pauses as though it were sending, then reports the same success the real
    // path does, which is also what the walkthrough needs to show.
    if (isDemoProjectId(projectId)) {
      await new Promise((resolve) => setTimeout(resolve, 700));
      setLoading(false);
      onSuccess(new Date(Date.now() + LINK_HOURS[dataRoute] * 60 * 60 * 1000).toISOString(), dataRoute);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await fetch('/.netlify/functions/send-ami-consent-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          projectId,
          contractorName,
          contractorLicense: companyProfile?.preparer_license || '',
          companyName,
          contractorEmail,
          customerName: customerName.trim(),
          // Phone comes from Project Info; this page no longer asks for it.
          customerPhone: (project.customer_phone || '').replace(/\D/g, '') || null,
          customerEmail,
          serviceAddress,
          utilityId: project.utility_id ?? null,
          utilityName,
          utilityEmail: dataRoute === 'self_download' ? '' : effectiveUtilityEmail,
          dataRoute,
          accountNumber: isBED
            ? joinBedAccount(bedCustomerId, bedLocationId)
            : accountNumber || null,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        setError(result.error || 'The link could not be sent. Please try again.');
        return;
      }

      // Persist the customer's email (and a name typed here) back to the project.
      if (!projectId.startsWith('local_') && !isDemoProjectId(projectId)) {
        const updates: Record<string, string> = {};
        if (customerEmail) updates.customer_email = customerEmail;
        if (!projectName && customerName.trim()) updates.customer_name = customerName.trim();        if (Object.keys(updates).length > 0) {
          await supabase.from('projects').update(updates).eq('id', projectId);
        }
      }

      onSuccess(result.expiresAt, dataRoute);
    } catch (err) {
      console.error('Get data send error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  const routeLabel =
    pilot ? `Request from ${pilot.contact_name} (pilot)`
    : dataRoute === 'self_download' ? 'Customer download'
    : formRoute ? 'Customer requests on the utility’s form'
    : dataRoute === 'customer_request' ? 'Customer requests by email'
    : dataRoute === 'email_request' ? 'Email request'
    : routes.connect ? 'Direct connection coming'
    : 'No route known yet';
  const timeLabel =
    pilot ? 'No customer step'
    : dataRoute === 'self_download' ? 'About five minutes for them'
    : dataRoute === 'email_request' ? 'Usually 2–5 business days'
    : formRoute ? `About ${REQUEST_FORM_BUSINESS_DAYS} business days after they submit`
    : dataRoute === 'customer_request' ? 'Depends on the utility’s reply'
    : null;
  const sendLabel =
    dataRoute === 'self_download' ? 'Send download link'
    : dataRoute === 'customer_request' ? 'Send request link'
    : 'Send authorization request';
  const displayUtility = utilityName || 'This utility';

  const switchClass = (route: LinkRoute) =>
    `px-3 py-1.5 text-sm font-medium transition-colors ${
      dataRoute === route ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <div
      data-testid="ami-request-modal"
      className="fixed inset-0 z-50 bg-gray-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="get-data-title"
    >
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="btn-ami-request-close"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 touch-manipulation"
          >
            <ArrowLeft size={16} />
            Back to project
          </button>
          <div className="min-w-0 ml-auto text-right">
            <h1 id="get-data-title" className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              Get interval data
            </h1>
            {(projectName || serviceAddress) && (
              <p className="text-xs text-gray-500 truncate">
                {[projectName, serviceAddress].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="get-data-page" data-route-primary={routes.primary ?? 'none'}>
        <section className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900 mr-1">{displayUtility}</h2>
            <span
              className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${
                dataRoute ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
              }`}
              data-testid="get-data-route-label"
            >
              {routeLabel}
            </span>
            {timeLabel && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">{timeLabel}</span>
            )}
            {dataRoute && routes.connect && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">Direct connection coming</span>
            )}
          </div>

          {bothRoutes && !pilot && (
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="group" aria-label="How the data gets here" data-testid="get-data-options">
              <button
                type="button"
                onClick={() => setDataRoute('self_download')}
                aria-pressed={dataRoute === 'self_download'}
                data-testid="get-data-option-self-download"
                className={switchClass('self_download')}
              >
                Customer download
              </button>
              <button
                type="button"
                onClick={() => setDataRoute('email_request')}
                aria-pressed={dataRoute === 'email_request'}
                data-testid="get-data-option-email-request"
                className={`${switchClass('email_request')} border-l border-gray-300`}
              >
                Email request
              </button>
            </div>
          )}

          {rollout && !pilot && (
            <p className="text-sm text-gray-700" data-testid={routes.connect ? 'get-data-connect-note' : 'get-data-rollout-note'}>
              {rollout}
            </p>
          )}
        </section>

        {pilot && (
          <>
            <ol className="space-y-2" data-testid="get-data-steps-summary">
              <Step n={1}>
                TrueLoad emails {pilot.contact_name} at {displayUtility} the customer&rsquo;s name, service address and account number. Your customer does nothing.
              </Step>
              <Step n={2}>{pilot.contact_name} replies with the data file attached.</Step>
              <Step n={3}>It loads into this project by itself, and you get the reply and the file by email as a backup.</Step>
            </ol>
            <p className="text-xs text-gray-500 pl-9" data-testid="get-data-pilot-ask">
              We ask for: {PILOT_DATA_ASK[0].charAt(0).toLowerCase() + PILOT_DATA_ASK[0].slice(1)}
            </p>
            <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5" data-testid="get-data-pilot-form">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Send the request</h3>
              <form onSubmit={handlePilotSubmit} className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                  <span className="text-gray-500 min-w-[7.5rem]">Customer</span>
                  {projectName ? (
                    <span className="text-gray-900">{projectName}</span>
                  ) : (
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Customer name"
                      aria-label="Customer name"
                      className={`${INPUT} w-64`}
                      data-testid="get-data-customer-name"
                    />
                  )}
                </div>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                  <span className="text-gray-500 min-w-[7.5rem]">Service address</span>
                  <span className="text-gray-900">{serviceAddress || 'Not set — add it under Project Info'}</span>
                </div>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                  <span className="text-gray-500 min-w-[7.5rem]">
                    Account number <span className="text-red-600">*</span>
                  </span>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    required
                    aria-label="Utility account number"
                    className={`${INPUT} w-48`}
                    data-testid="get-data-pilot-account"
                  />
                  <span className="text-xs text-gray-500">From the customer&rsquo;s bill</span>
                </div>
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="btn-send-pilot-request"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors font-medium touch-manipulation"
                >
                  {loading ? 'Sending…' : `Request from ${displayUtility}`}
                </button>
              </form>
            </section>
          </>
        )}

        {!pilot && dataRoute === 'email_request' && (
          <ol className="space-y-2" data-testid="get-data-steps-summary">
            <Step n={1}>Your customer approves the release from a link TrueLoad emails them. Good for 72 hours.</Step>
            <Step n={2}>TrueLoad emails {displayUtility}, with you as the contact.</Step>
            <Step n={3}>The utility sends you the file. Upload it on the Existing Load tab.</Step>
          </ol>
        )}

        {!pilot && formRoute && (
          <ol className="space-y-2" data-testid="get-data-steps-summary">
            <Step n={1}>
              Your customer opens the link and submits {displayUtility}’s request form, which TrueLoad fills in with what the project holds: their name, contact details, address{isBED ? ', Customer ID and Location ID' : ' and account number'}. They sign the utility’s waiver themselves.
            </Step>
            {receivingOn ? (
              <>
                <Step n={2}>{displayUtility} sends the data to TrueLoad, usually within {REQUEST_FORM_BUSINESS_DAYS} business days of the request.</Step>
                <Step n={3}>It loads into this project by itself, and you get a copy by email. The link stays open 30 days.</Step>
              </>
            ) : (
              <>
                <Step n={2}>{displayUtility} emails the data to you, usually within {REQUEST_FORM_BUSINESS_DAYS} business days of the request.</Step>
                <Step n={3}>Upload the file on the Existing Load tab when it arrives. The link stays open 30 days.</Step>
              </>
            )}
          </ol>
        )}

        {!pilot && dataRoute === 'customer_request' && !formRoute && (
          <ol className="space-y-2" data-testid="get-data-steps-summary">
            <Step n={1}>
              Your customer opens the link and sends {displayUtility} a request from their own email. TrueLoad writes it for them, with their address{isBED ? ', Customer ID and Location ID' : ' and account number'}.
            </Step>
            <Step n={2}>{displayUtility} emails the file to your customer. They upload it on the same page and send it. That is their authorization.</Step>
            <Step n={3}>The readings land in this project and you get an email. The link stays open 30 days.</Step>
          </ol>
        )}

        {!pilot && dataRoute === 'self_download' && (
          <div className="space-y-2">
            <ol className="space-y-2" data-testid="get-data-steps-summary">
              <Step n={1}>
                Your customer opens the link and downloads the file from {routes.unlisted ? 'their utility’s' : `${displayUtility}’s`} portal. About five minutes.
              </Step>
              <Step n={2}>They drop it on the same page and send it. That is their authorization.</Step>
              <Step n={3}>The readings land in this project and you get an email. The link stays open 14 days.</Step>
            </ol>
            <div className="pl-9 text-sm">
              <button
                type="button"
                onClick={() => setPreviewOpen((o) => !o)}
                data-testid="get-data-steps-toggle"
                aria-expanded={previewOpen}
                className="inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-900 underline"
              >
                {previewOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {previewOpen ? 'Hide the preview' : 'Preview what your customer sees'}
              </button>
              {previewOpen && (
                <div className="mt-2 space-y-1.5">
                  <ol className="list-decimal pl-5 space-y-1 text-gray-800" data-testid="get-data-steps">
                    {steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                    {portal && (
                      <li>
                        Portal:{' '}
                        <a
                          href={portal}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-700 underline"
                        >
                          {portal.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                          <ExternalLink size={12} />
                        </a>
                      </li>
                    )}
                  </ol>
                  {(format || history) && (
                    <p className="text-xs text-gray-600">
                      {[format ? `Exports ${format}` : '', history ? history.charAt(0).toUpperCase() + history.slice(1) : '']
                        .filter(Boolean)
                        .join(' · ')}
                      .
                    </p>
                  )}
                  {!routes.unlisted && !generic && (
                    <p className="text-xs text-gray-500" data-testid="get-data-verification">
                      {verified
                        ? 'Steps confirmed by a homeowner.'
                        : 'Steps written from the utility’s help pages and not yet confirmed by a homeowner.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {!pilot && !dataRoute && (
          <p className="text-sm text-gray-700" data-testid="get-data-no-route">
            TrueLoad has no interval-data route for {displayUtility} yet.
          </p>
        )}

        {!pilot && dataRoute && (
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5" data-testid="get-data-send-form">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Send the link</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              {!projectName && (
                <div>
                  <label htmlFor="get-data-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Customer name <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="get-data-name"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className={`${INPUT} w-full`}
                    data-testid="get-data-customer-name"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <label htmlFor="get-data-email" className="block text-sm font-medium text-gray-700 mb-1">
                    {projectName ? `To ${projectName} at` : 'Customer email'} <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="get-data-email"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                    placeholder="customer@example.com"
                    className={`${INPUT} w-full`}
                    data-testid="get-data-customer-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="btn-send-ami-request"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors font-medium whitespace-nowrap touch-manipulation"
                >
                  {loading ? 'Sending…' : sendLabel}
                </button>
              </div>

              {(dataRoute === 'email_request' || (dataRoute === 'customer_request' && !formRoute)) && (
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm" data-testid="get-data-utility-contact">
                  <span className="text-gray-500 min-w-[7.5rem]">
                    {dataRoute === 'customer_request' ? 'Customer writes to' : 'Utility contact'}
                  </span>
                  {editingContact ? (
                    <input
                      type="email"
                      value={customUtilityEmail}
                      onChange={(e) => setCustomUtilityEmail(e.target.value)}
                      required
                      autoFocus
                      className={`${INPUT} w-full max-w-sm`}
                      data-testid="get-data-utility-contact-input"
                    />
                  ) : (
                    <>
                      <span className="text-gray-900 break-all">
                        {utilityEmail || 'No address on file'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingContact(true)}
                        className="text-blue-700 underline hover:text-blue-900"
                        data-testid="get-data-utility-contact-change"
                      >
                        {utilityEmail ? 'Change' : 'Enter one'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {isBED ? (
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                  <span className="text-gray-500 min-w-[7.5rem]">Account</span>
                  <input
                    type="text"
                    value={bedCustomerId}
                    onChange={(e) => setBedCustomerId(e.target.value)}
                    placeholder="Customer ID"
                    aria-label="Customer ID"
                    className={`${INPUT} w-36`}
                  />
                  <input
                    type="text"
                    value={bedLocationId}
                    onChange={(e) => setBedLocationId(e.target.value)}
                    placeholder="Location ID"
                    aria-label="Location ID"
                    className={`${INPUT} w-36`}
                  />
                  <span className="text-xs text-gray-500">
                    {formRoute ? 'Optional, from the bill. Both are needed to fill the form; otherwise your customer types them.' : 'Optional, from the bill'}
                  </span>
                </div>
              ) : (
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                  <span className="text-gray-500 min-w-[7.5rem]">Account number</span>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="Optional"
                    aria-label="Utility account number"
                    className={`${INPUT} w-48`}
                  />
                  <span className="text-xs text-gray-500">From the bill</span>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </form>
          </section>
        )}

        <div className="text-sm text-gray-700 space-y-1.5" data-testid="get-data-other">
          <p>
            <span className="font-semibold">Have the file already?</span>{' '}
            <button type="button" onClick={onClose} className="text-blue-700 underline hover:text-blue-900">
              Upload it on the Existing Load tab.
            </button>
          </p>
          <p>
            <span className="font-semibold">No utility data?</span> A 30-day recording at the panel counts.{' '}
            <button
              type="button"
              onClick={() => openHelp('direct-measurement')}
              className="text-blue-700 underline hover:text-blue-900"
            >
              How that works
            </button>
          </p>
        </div>

        {!routes.connect && !pilot && (
          <UtilityRequestPrompt project={project} variant="ami-data" tone={routes.primary ? 'blue' : 'amber'}>
            <div className="flex items-start gap-2">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
              <p>
                <span className="font-semibold">Direct connections come next.</span>{' '}
                TrueLoad has no automatic connection with {displayUtility} yet. Telling us you
                work in this territory decides which utilities get one first.
              </p>
            </div>
          </UtilityRequestPrompt>
        )}
      </div>
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="grid grid-cols-[26px_1fr] gap-2.5 items-start text-sm text-gray-900">
      <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{n}</span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}
